import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { EnergyMapService } from '@app/energy-map/services/energy-map-service';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { UserService } from '@app/shared/services/api/user.service';
import { AuthService } from '@app/shared/services/auth.service';
import { CookieConfigService } from '@app/shared/services/storage/cookie-config.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { environment } from '@env/environment';
import { Subject, Subscription } from 'rxjs';
import { MunicipalityAlarm } from '@app/energy-guard/models/alarms';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { Tenant } from '@app/shared/enums';
import { StringUtility } from '@app/shared/string-utility';
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Input() small = false;
  public firstname: string = '';
  public lastname: string = '';
  public municipalities: MapStoreMunicipality[] = [];
  public selectedMunicipality: Partial<MapStoreMunicipality> = { guid: '' };
  public selectedMunicipalityText = '';
  public hideDropdown = false;
  public municipalityAlarm: MunicipalityAlarm = { hasActiveAlarm: false };
  public companyLogo: any;
  public isDsoUser = false;
  public isTenantLew: boolean = false;

  private routerSubscription: Subscription = new Subscription();

  constructor(
    public authService: AuthService,
    private energyMapService: EnergyMapService,
    private router: Router,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private userService: UserService,
    private blobStorageService: AzureBlobStorageService,
    private cookieConfigService: CookieConfigService
  ) {
    this.isTenantLew = StringUtility.areStringsIdentical(environment.tenant, Tenant.LECHWERKE);
  }

  public ngOnInit(): void {
    this.userService
      .getByUserGuid(this.authService.currentUser.guid)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((u) => {
        this.firstname = u.firstname;
        this.lastname = u.lastname;
      });

    this.authService.firstName$.pipe(takeUntil(this.unsubscribe$)).subscribe((a) => (this.firstname = a));
    this.authService.lastName$.pipe(takeUntil(this.unsubscribe$)).subscribe((a) => (this.lastname = a));

    this.userMunicipalitiesService.municipalities$.pipe(takeUntil(this.unsubscribe$)).subscribe((municipalities: MapStoreMunicipality[]) => {
      this.municipalities = [...municipalities];
      this.setMunicipalityFromCookie();
    });

    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((municipality: MapStoreMunicipality) => {
        this.selectedMunicipality = municipality;
        this.selectedMunicipalityText = this.formatName(municipality);
        this.cookieConfigService.municipality = { key: this.selectedMunicipality.key as string };
      });

    this.companyLogo = this.blobStorageService.getBlobImageSafeUrl(`${environment.tenant}/assets/header-logo.png`);

    this.hideDropdown = this.checkHideDropdown();
    this.routerSubscription = this.router.events.subscribe((event: any) => {
      this.navigationInterceptor(event);
    });

    this.isDsoUser = this.authService.isDsoUser;
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  public setMunicipality(municipality: MapStoreMunicipality): void {
    this.selectedMunicipality = this.municipalities.filter((value: MapStoreMunicipality) => {
      this.energyMapService.municipalityChanged(true);
      return this.formatName(value).toLowerCase() === this.formatName(municipality).toLowerCase();
    })[0];
    this.cookieConfigService.municipality = { key: this.selectedMunicipality.key as string };

    this.userMunicipalitiesService.updateMunicipality(this.selectedMunicipality as MapStoreMunicipality);
    this.authService.applyMunicipalityRestrictions(municipality.guid);
  }

  public navigationInterceptor(event: any): void {
    if (event instanceof NavigationEnd) {
      this.hideDropdown = this.checkHideDropdown();
    }
  }

  private formatName(municipality: MapStoreMunicipality): string {
    if (!this.authService.isMunicipalityWorker) {
      return `${municipality.name} (${municipality.key})`;
    } else {
      return `${municipality.name}`;
    }
  }

  private checkHideDropdown(): boolean {
    return (
      (this.router.url.indexOf('messdaten') >= 0 && this.router.url.indexOf('connectionobjectnumber') >= 0) ||
      (this.router.url.indexOf('messdaten') >= 0 && this.router.url.indexOf('sensor-details') >= 0) ||
      (this.router.url.indexOf('verbrauch') >= 0 && this.router.url.indexOf('connectionobjectnumber') >= 0)
    );
  }

  private setMunicipalityFromCookie() {
    if (this.cookieConfigService.municipality) {
      const municipality = this.municipalities.find((municipality: MapStoreMunicipality) => municipality.key === this.cookieConfigService.municipality.key);
      if (municipality) {
        this.setMunicipality(municipality);
      }
    }
  }
}
